import { useState, useCallback, useEffect } from 'react';
import AdSenseBanner from './components/AdSenseBanner';
import DashboardShell from './comprasLoader/DashboardShell';
import AdGate from './comprasLoader/AdGate';
import UploadZone from './comprasLoader/UploadZone';
import CostaRicaPanel from './comprasLoader/CostaRicaPanel';
import ElSalvadorPanel from './comprasLoader/ElSalvadorPanel';
import { processComprasFiles } from './comprasLoader/detectSchema';
import { mergeCostaRicaInvoices } from './comprasLoader/parseCostaRica';
import { mergeElSalvadorInvoices, applyVoidEvents } from './comprasLoader/parseElSalvador';

const MIN_PROCESSING_MS = 3000;

export default function ComprasLoader() {
  const [crInvoices, setCrInvoices] = useState([]);
  const [svInvoices, setSvInvoices] = useState([]);
  const [crErrors, setCrErrors] = useState([]);
  const [svErrors, setSvErrors] = useState([]);
  const [generalErrors, setGeneralErrors] = useState([]);
  const [isDragging, setIsDragging] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  // Ad gate: { files, id, waitDone, processed } while an upload is in progress.
  const [gate, setGate] = useState(null);

  const processFiles = useCallback(async (files) => {
    if (!files || !files.length) return;
    setIsProcessing(true);
    try {
      const { costaRica, elSalvador, voids, errors } = await processComprasFiles(files);

      if (costaRica.length) {
        setCrInvoices(prev => mergeCostaRicaInvoices(prev, costaRica));
      }
      if (elSalvador.length || voids.length) {
        setSvInvoices(prev => {
          const merged = mergeElSalvadorInvoices(prev, elSalvador);
          return voids.length ? applyVoidEvents(merged, voids) : merged;
        });
      }

      const crFailed = errors.filter(e => e.country === 'CR');
      const svFailed = errors.filter(e => e.country === 'SV');
      const otherFailed = errors.filter(e => e.country !== 'CR' && e.country !== 'SV');
      if (crFailed.length) setCrErrors(prev => [...prev, ...crFailed]);
      if (svFailed.length) setSvErrors(prev => [...prev, ...svFailed]);
      if (otherFailed.length) setGeneralErrors(prev => [...prev, ...otherFailed]);
    } finally {
      setIsProcessing(false);
    }
  }, []);

  // Every upload goes through the ad gate: bar fills (slower for more files),
  // then files are processed with the ad still visible, then the gate closes.
  const requestFiles = useCallback((files) => {
    if (!files || !files.length || gate) return;
    setGate({ files: Array.from(files), id: Date.now(), waitDone: false, processed: false });
  }, [gate]);

  const onWaitDone = useCallback(() => {
    setGate(g => (g ? { ...g, waitDone: true } : g));
  }, []);

  useEffect(() => {
    if (!gate || !gate.waitDone || gate.processed || gate.running) return;
    setGate(g => ({ ...g, running: true }));
    // Keep the ad visible for at least MIN_PROCESSING_MS even when parsing is instant.
    Promise.all([processFiles(gate.files), new Promise(r => setTimeout(r, MIN_PROCESSING_MS))])
      .finally(() => setGate(null));
  }, [gate, processFiles]);

  const clearAll = useCallback(() => {
    setCrInvoices([]);
    setSvInvoices([]);
    setCrErrors([]);
    setSvErrors([]);
    setGeneralErrors([]);
  }, []);

  const hasAnyData = crInvoices.length > 0 || svInvoices.length > 0
    || crErrors.length > 0 || svErrors.length > 0 || generalErrors.length > 0;

  return (
    <DashboardShell>
      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-500">
          Arrastra tus comprobantes de Costa Rica o El Salvador — se detectan y separan automáticamente.
        </p>
        {hasAnyData && (
          <button onClick={clearAll} className="text-sm text-gray-400 hover:text-gray-600 transition-colors px-2 py-1.5 shrink-0">
            Limpiar todo
          </button>
        )}
      </div>

      <UploadZone
        isDragging={isDragging}
        isProcessing={isProcessing || !!gate}
        onFiles={requestFiles}
        onDragState={setIsDragging}
      />

      <AdSenseBanner slot="7397489309" />

      {generalErrors.length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-red-700 font-semibold text-sm">
              {generalErrors.length} archivo{generalErrors.length !== 1 ? 's' : ''} no reconocido{generalErrors.length !== 1 ? 's' : ''}
            </h3>
            <button onClick={() => setGeneralErrors([])} className="text-red-400 hover:text-red-600 text-xs">
              Descartar
            </button>
          </div>
          <ul className="space-y-1">
            {generalErrors.map((e, i) => (
              <li key={i} className="text-red-600 text-xs">
                <span className="font-mono font-medium">{e.fileName}</span>
                {' — '}{e.error}
              </li>
            ))}
          </ul>
        </div>
      )}

      <CostaRicaPanel
        invoices={crInvoices}
        errors={crErrors}
        onDismissErrors={() => setCrErrors([])}
      />

      <ElSalvadorPanel
        invoices={svInvoices}
        errors={svErrors}
        onDismissErrors={() => setSvErrors([])}
      />

      <AdSenseBanner slot="7296462661" />
      {gate && (
        <AdGate key={gate.id} fileCount={gate.files.length}
          phase={gate.waitDone ? 'processing' : 'waiting'} onWaitDone={onWaitDone} />
      )}
    </DashboardShell>
  );
}
